const DEFAULT_WEIGHT = 0.5;

export const CREATIVE_BIBLE_SECTIONS = Object.freeze([
  {
    key: 'direction',
    label: 'Direction',
    fieldPaths: ['thesis', 'pillars'],
  },
  {
    key: 'characters',
    label: 'Characters',
    fieldPaths: ['anchors'],
  },
  {
    key: 'palette',
    label: 'Palette',
    fieldPaths: ['look.palette', 'look.lighting', 'look.grading'],
  },
  {
    key: 'look',
    label: 'Look & Style',
    fieldPaths: [
      'look.materials',
      'look.texture',
      'look.composition',
      'look.camera',
      'look.motion',
    ],
  },
  {
    key: 'rules',
    label: 'Rules',
    // `rules.neverInstead` is ONE path, never split against a rule's own text. It rides with
    // the rules section so locking Rules also protects the conversions, and it reaches
    // SYNTHESIS_WRITABLE_FIELD_PATHS from here.
    fieldPaths: ['rules.must', 'rules.never', 'rules.preserve', 'rules.prefer', 'rules.neverInstead'],
  },
  {
    key: 'feel',
    label: 'Feel',
    fieldPaths: ['feel.register'],
  },
  {
    key: 'outputs',
    label: 'Outputs & Defaults',
    fieldPaths: [
      'outputDefaults.preferredAspectRatio',
      'outputDefaults.defaultCameraMovement',
      'outputDefaults.defaultSpeedPreset',
      'defaultStyleAssetId',
    ],
  },
]);

export const CREATIVE_BIBLE_SECTION_KEYS = Object.freeze(
  CREATIVE_BIBLE_SECTIONS.map((section) => section.key)
);

const SECTION_BY_KEY = new Map(CREATIVE_BIBLE_SECTIONS.map((section) => [section.key, section]));

function clampWeight(value) {
  const numeric = typeof value === 'number' ? value : Number.parseFloat(value);
  if (!Number.isFinite(numeric)) return DEFAULT_WEIGHT;
  if (numeric < 0) return 0;
  if (numeric > 1) return 1;
  return numeric;
}

function createDefaultSectionControl() {
  return { weight: DEFAULT_WEIGHT, locked: false };
}

export function createDefaultSectionControls() {
  const controls = {};
  for (const section of CREATIVE_BIBLE_SECTIONS) {
    controls[section.key] = createDefaultSectionControl();
  }
  return controls;
}

export function normalizeSectionControls(input) {
  const source = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const controls = {};
  for (const section of CREATIVE_BIBLE_SECTIONS) {
    const candidate = source[section.key];
    if (candidate && typeof candidate === 'object') {
      controls[section.key] = {
        weight: clampWeight(candidate.weight),
        locked: candidate.locked === true,
      };
    } else {
      controls[section.key] = createDefaultSectionControl();
    }
  }
  return controls;
}

export function updateSectionControl(controls, sectionKey, patch = {}) {
  const normalized = normalizeSectionControls(controls);
  if (!SECTION_BY_KEY.has(sectionKey)) return normalized;
  const current = normalized[sectionKey];
  normalized[sectionKey] = {
    weight: Object.prototype.hasOwnProperty.call(patch, 'weight')
      ? clampWeight(patch.weight)
      : current.weight,
    locked: Object.prototype.hasOwnProperty.call(patch, 'locked')
      ? patch.locked === true
      : current.locked,
  };
  return normalized;
}

export function listLockedSectionKeys(controls) {
  const normalized = normalizeSectionControls(controls);
  return CREATIVE_BIBLE_SECTION_KEYS.filter((key) => normalized[key].locked);
}

export function compileLockedFieldPathsFromSections(controls) {
  const lockedKeys = listLockedSectionKeys(controls);
  const paths = new Set();
  for (const key of lockedKeys) {
    for (const path of SECTION_BY_KEY.get(key).fieldPaths) {
      paths.add(path);
    }
  }
  return Array.from(paths);
}

export function compileRewriteProtection(draft = {}) {
  const fieldPaths = new Set();
  const explicitLockedFieldPaths = Array.isArray(draft.lockedFieldPaths) ? draft.lockedFieldPaths : [];
  for (const path of explicitLockedFieldPaths) {
    if (path) fieldPaths.add(path);
  }
  for (const path of compileLockedFieldPathsFromSections(draft.sectionControls)) {
    fieldPaths.add(path);
  }
  return {
    lockedSectionKeys: listLockedSectionKeys(draft.sectionControls),
    lockedFieldPaths: Array.from(fieldPaths),
  };
}
